<svelte:options immutable={true} />

<script>
  import { createEventDispatcher, onMount } from "svelte";
  import { pannable } from "../../actions/pannable.js";
  import { tapout } from "../../actions/tapout.js";
  import Toolbar from "../Toolbar.svelte";
  import { MAX_OBJECT_DIMENSION } from "../../lib/constants.js";
  import { editor } from "../../stores/editor.js";

  export let image;
  export let width;
  export let height;
  export let x;
  export let y;
  export let opacity = 1;
  export let keepRatio = true;
  export let pageScale = 1;
  export let selected = false;
  export let showToolbar = true;
  export let hideBorders = false;

  const dispatch = createEventDispatcher();

  let wasEditing = false;
  $: {
    if (isEditing !== wasEditing) {
      if (isEditing) dispatch("select");
      else dispatch("blur");
      wasEditing = isEditing;
    }
  }

  let startPointerX = 0;
  let startPointerY = 0;
  let canvasElement;

  let isEditing = false;
  let operation = "";
  let resizeDirection = "";

  let translateX = 0;
  let translateY = 0;
  let draftOpacity = opacity;
  let deltaWidth = 0;
  let deltaHeight = 0;

  let aspectRatio = null;

  function initialize() {
    canvasElement.width = width;
    canvasElement.height = height;
    canvasElement.getContext("2d").drawImage(image, 0, 0, width, height);
  }

  function onPanStart(event) {
    isEditing = true;
    startPointerX = event.detail.x;
    startPointerY = event.detail.y;

    if (event.detail.target === event.currentTarget) {
      operation = "move";
      return;
    }

    operation = "scale";
    resizeDirection = event.detail.target.dataset.direction || "";
  }

  function onPanMove(event) {
    const pointerDeltaX = (event.detail.x - startPointerX) / pageScale;
    const pointerDeltaY = (event.detail.y - startPointerY) / pageScale;

    if (operation === "move") {
      translateX = pointerDeltaX;
      translateY = pointerDeltaY;
      return;
    }

    if (operation !== "scale") return;

    const next = calculateResizeDelta({
      direction: resizeDirection,
      pointerDeltaX,
      pointerDeltaY,
      width,
      height,
      aspectRatio,
    });

    translateX = next.translateX;
    translateY = next.translateY;
    deltaWidth = next.deltaWidth;
    deltaHeight = next.deltaHeight;
  }

  function onPanEnd() {
    if (operation === "move") {
      dispatch("update", {
        x: x + translateX,
        y: y + translateY,
      });
    } else if (operation === "scale") {
      dispatch("update", {
        x: x + translateX,
        y: y + translateY,
        width: width + deltaWidth,
        height: height + deltaHeight,
      });
    }

    operation = "";
    resizeDirection = "";
    translateX = 0;
    translateY = 0;
    deltaWidth = 0;
    deltaHeight = 0;
  }

  function lockAspectRatio(deltaWidth, deltaHeight, aspectRatio) {
    const deltaHeightFromWidth = (width + deltaWidth) / aspectRatio - height;

    if (deltaHeight > deltaHeightFromWidth) {
      const deltaWidthFromHeight = (height + deltaHeight) * aspectRatio - width;
      return [deltaWidthFromHeight, deltaHeight];
    }

    return [deltaWidth, deltaHeightFromWidth];
  }

  function calculateResizeDelta({
    direction,
    pointerDeltaX,
    pointerDeltaY,
    aspectRatio,
  }) {
    const fromLeft = direction.includes("left");
    const fromRight = direction.includes("right");
    const fromTop = direction.includes("top");
    const fromBottom = direction.includes("bottom");

    const hasHorizontal = fromLeft || fromRight;
    const hasVertical = fromTop || fromBottom;

    let deltaWidth = fromLeft ? -pointerDeltaX : fromRight ? pointerDeltaX : 0;
    let deltaHeight = fromTop ? -pointerDeltaY : fromBottom ? pointerDeltaY : 0;

    let currentAspectRatio = aspectRatio;
    if (keepRatio && currentAspectRatio === null) {
      currentAspectRatio = width / height;
    }

    if (currentAspectRatio !== null) {
      if (hasHorizontal && !hasVertical) {
        deltaHeight = (width + deltaWidth) / currentAspectRatio - height;
      } else if (hasVertical && !hasHorizontal) {
        deltaWidth = (height + deltaHeight) * currentAspectRatio - width;
      } else if (hasHorizontal && hasVertical) {
        [deltaWidth, deltaHeight] = lockAspectRatio(
          deltaWidth,
          deltaHeight,
          currentAspectRatio,
        );
      }
    }

    const translateX = fromLeft ? -deltaWidth : 0;
    const translateY = fromTop ? -deltaHeight : 0;

    return {
      translateX,
      translateY,
      deltaWidth,
      deltaHeight,
    };
  }

  function onBlur() {
    isEditing = false;
  }

  function toggleKeepRatio() {
    dispatch("update", { keepRatio: !keepRatio });
  }

  function onDelete() {
    dispatch("delete");
  }

  onMount(initialize);

  onMount(() => {
    function onKeyDown(event) {
      if (event.key === "Shift") {
        aspectRatio = (width + deltaWidth) / (height + deltaHeight);
      }
    }

    function onKeyUp(event) {
      if (event.key === "Shift") {
        aspectRatio = null;
      }
    }

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  });
</script>

{#if isEditing && showToolbar}
  <Toolbar>
    <div class="flex items-center px-1">
      <div class="flex items-center border-l border-gray-200 pl-2 ml-1">
        <button
          on:click={toggleKeepRatio}
          class="w-6 h-6 flex items-center justify-center rounded transition-colors hover:bg-gray-100 {keepRatio
            ? 'text-indigo-600'
            : 'text-gray-400'}"
          title="維持圖形比例"
        >
          {#if keepRatio}
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              ><rect x="3" y="11" width="18" height="11" rx="2" ry="2"
              ></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg
            >
          {:else}
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              ><rect x="3" y="11" width="18" height="11" rx="2" ry="2"
              ></rect><path d="M7 11V7a5 5 0 0 1 9.9-1"></path></svg
            >
          {/if}
        </button>
      </div>
      <div class="flex items-center gap-2 border-l border-gray-200 px-3">
        <span class="text-xs text-gray-500">透明度:</span>
        <input
          type="range"
          min="0"
          max="1"
          step="0.05"
          bind:value={draftOpacity}
          on:input={() => dispatch("update", { opacity: Number(draftOpacity) })}
          class="w-16 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          title="調整透明度"
        />
      </div>
    </div>
  </Toolbar>
{/if}

<div
  use:tapout
  on:tapout={onBlur}
  on:mousedown={() => (isEditing = true)}
  on:touchstart={() => (isEditing = true)}
  class="absolute left-0 top-0 select-none"
  class:outline={isEditing}
  class:outline-2={isEditing}
  class:outline-blue-500={isEditing}
  style="width: {width + deltaWidth}px; height: {height +
    deltaHeight}px; transform: translate({x + translateX}px, {y +
    translateY}px); opacity: {draftOpacity};"
>
  <div
    use:pannable
    on:panstart={onPanStart}
    on:panmove={onPanMove}
    on:panend={onPanEnd}
    class="absolute w-full h-full cursor-grab"
    class:cursor-grabbing={operation === "move"}
    class:operation
  >
    {#if !hideBorders || selected || isEditing}
      <div
        data-direction="left"
        class="resize-border h-full w-1 left-0 top-0 border-l cursor-ew-resize"
      />
      <div
        data-direction="top"
        class="resize-border w-full h-1 left-0 top-0 border-t cursor-ns-resize"
      />
      <div
        data-direction="bottom"
        class="resize-border w-full h-1 left-0 bottom-0 border-b cursor-ns-resize"
      />
      <div
        data-direction="right"
        class="resize-border h-full w-1 right-0 top-0 border-r cursor-ew-resize"
      />
      <div
        data-direction="left-top"
        class="resize-corner left-0 top-0 cursor-nwse-resize transform
        -translate-x-1/2 -translate-y-1/2 md:scale-25"
      />
      <div
        data-direction="right-top"
        class="resize-corner right-0 top-0 cursor-nesw-resize transform
        translate-x-1/2 -translate-y-1/2 md:scale-25"
      />
      <div
        data-direction="left-bottom"
        class="resize-corner left-0 bottom-0 cursor-nesw-resize transform
        -translate-x-1/2 translate-y-1/2 md:scale-25"
      />
      <div
        data-direction="right-bottom"
        class="resize-corner right-0 bottom-0 cursor-nwse-resize transform
        translate-x-1/2 translate-y-1/2 md:scale-25"
      />
    {/if}
  </div>
  <canvas class="w-full h-full" bind:this={canvasElement} />
</div>

<style>
  .operation {
    background-color: rgba(0, 0, 0, 0.3);
  }
  .resize-border {
    @apply absolute border-dashed border-gray-600;
  }
  .resize-corner {
    @apply absolute w-10 h-10 bg-blue-300 rounded-full;
  }
</style>
